import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Input, message, Switch, InputNumber } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../../Components/Cards/InputCard";
import refuelIcon from "../../../../Assets/Icons/Container.svg";
import cardIcon from "../../../../Assets/Icons/credit_card.svg";
import resetIcon from "../../../../Assets/Icons/reset.svg";
import {
  getVehicleById,
  updateVehicle,
  getFuelTypes,
  getVehicleDocumentTypes,
  getVehicleTypes,
  getNavixyApiKeys,
  getNavixyTrackers,
  getNavixyOdometer,
  deleteVehicleDocument,
} from "../../../../API/FleetManager/Vehicles";
import { getDriversLookup } from "../../../../API/FleetManager/Drivers";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const toNavixyKeyOptions = (res) =>
  (res?.data?.data || [])
    .map((item) => {
      if (typeof item === "string") return { value: item, label: item };
      const value = item?.value ?? item?.api_key ?? item?.key;
      return value
        ? { value: String(value), label: String(item?.label ?? value) }
        : null;
    })
    .filter(Boolean);

const toNavixyTrackerOptions = (res) =>
  (res?.data?.data || [])
    .map((item) => {
      if (typeof item !== "object" || item === null) {
        return { value: String(item), label: String(item) };
      }
      const value = item.id ?? item.tracker_id ?? item.value;
      if (value === undefined || value === null) return null;
      const name = item.label ?? item.name;
      return {
        value: String(value),
        label: name ? `${name} (${value})` : String(value),
      };
    })
    .filter(Boolean);

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.entries(data)
    .filter(([, msgs]) => Array.isArray(msgs) && msgs.length)
    .map(([field, msgs]) => `${field}: ${msgs.join(" ")}`);

  return fieldErrors.length ? fieldErrors.join(" | ") : fallback;
};

const DRIVER_FIELDS_NOTE = "Filled in automatically from the assigned driver.";

const EditVehicle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [form] = Form.useForm();
  const watchedTrackingStatus = Form.useWatch("trackingStatus", form);
  const [navixyKeyOptions, setNavixyKeyOptions] = useState([]);
  const [navixyKeysLoading, setNavixyKeysLoading] = useState(false);
  const [trackerOptions, setTrackerOptions] = useState([]);
  const [trackersLoading, setTrackersLoading] = useState(false);
  const watchedNavixyKey = Form.useWatch("navixyApiKey", form);

  const [odometerNote, setOdometerNote] = useState(null);

  const fetchNavixyOdometer = async (apiKey, trackerId) => {
    if (!apiKey || !trackerId) {
      setOdometerNote(null);
      return;
    }
    setOdometerNote({ type: "loading", text: "Fetching reading from Navixy..." });
    try {
      const res = await getNavixyOdometer(apiKey, trackerId);
      const reading = res?.data?.data?.odometer;
      if (Math.floor(res?.status / 100) === 2 && reading != null) {
        form.setFieldsValue({ odometer: String(reading) });
        form.setFields([{ name: "odometer", errors: [] }]);
        setOdometerNote({
          type: "success",
          text: "Fetched from Navixy. You can change it if needed.",
        });
      } else {
        setOdometerNote({
          type: "warning",
          text:
            res?.data?.message ||
            "Navixy has no odometer reading for this tracker yet. Please enter it manually.",
        });
      }
    } catch (err) {
      setOdometerNote({
        type: "warning",
        text:
          err?.response?.data?.message ||
          "Couldn't fetch the reading from Navixy. Please enter it manually.",
      });
    }
  };

  const [creditData, setCreditData] = useState({
    fuel_card_id: null,
    is_active: false,
    card_number: "-",
    daily_limit: 0,
    daily_limit_enabled: false,
    monthly_limit: 0,
    monthly_limit_enabled: false,
    weekly_limit: 0,
    weekly_limit_enabled: false,
    weekend_limit: 0,
    weekend_limit_enabled: false,
    allow_any_driver: false,
    auto_reset: false,
  });

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverDetails, setDriverDetails] = useState({});
  const [currentDriver, setCurrentDriver] = useState(null);
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fetchedVehicleRef = useRef(null);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getDriversLookup(passedId);
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDriverOptions(list.map((d) => ({ label: d.name, value: d.id })));
          setDriverDetails(Object.fromEntries(list.map((d) => [d.id, d])));
        }
      } catch (err) {
        console.error("Failed to load drivers:", err);
      }
    };

    fetchDrivers();
  }, [passedId]);

  const watchedDriver = Form.useWatch("driver", form);

  useEffect(() => {
    const driver = driverDetails[watchedDriver];
    if (driver) {
      form.setFieldsValue({
        jobTitle: driver.job_title,
        address: driver.address,
      });
    }
  }, [watchedDriver, driverDetails, form]);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      try {
        const res = await getFuelTypes();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setFuelTypeOptions(list.map((f) => ({ label: f.name, value: f.id })));
        }
      } catch (err) {
        console.error("Failed to load fuel types:", err);
      }
    };

    fetchFuelTypes();
  }, []);

  useEffect(() => {
    if (watchedTrackingStatus !== "linked" || navixyKeyOptions.length) return;

    const fetchNavixyKeys = async () => {
      setNavixyKeysLoading(true);
      try {
        const res = await getNavixyApiKeys();
        if (Math.floor(res?.status / 100) === 2) {
          const options = toNavixyKeyOptions(res);
          setNavixyKeyOptions(options);
          if (options.length && !form.getFieldValue("navixyApiKey")) {
            form.setFieldsValue({ navixyApiKey: options[0].value });
          }
        }
      } catch (err) {
        message.error(
          getErrorMessage(
            err?.response?.data,
            "Failed to load Navixy API keys.",
          ),
        );
      } finally {
        setNavixyKeysLoading(false);
      }
    };

    fetchNavixyKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedTrackingStatus]);

  useEffect(() => {
    if (!watchedNavixyKey) {
      setTrackerOptions([]);
      return;
    }

    let cancelled = false;
    const fetchTrackers = async () => {
      setTrackersLoading(true);
      try {
        const res = await getNavixyTrackers(watchedNavixyKey, passedId);
        if (!cancelled && Math.floor(res?.status / 100) === 2) {
          setTrackerOptions(toNavixyTrackerOptions(res));
        }
      } catch (err) {
        if (!cancelled) {
          setTrackerOptions([]);
          message.error(
            getErrorMessage(
              err?.response?.data,
              "Failed to load Navixy trackers.",
            ),
          );
        }
      } finally {
        if (!cancelled) setTrackersLoading(false);
      }
    };

    fetchTrackers();
    return () => {
      cancelled = true;
    };
  }, [watchedNavixyKey, passedId]);

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const res = await getVehicleTypes();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setVehicleTypeOptions(
            list.map((t) => ({ value: t.value, label: t.label })),
          );
        }
      } catch (err) {
        console.error("Failed to load vehicle types:", err);
      }
    };

    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const res = await getVehicleDocumentTypes();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDocumentTypeOptions(
            list.map((t) => ({
              value: t.document_name,
              label: t.document_name,
              allowedFormats: t.allowed_formats || [],
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load document types:", err);
      }
    };

    fetchDocumentTypes();
  }, []);

  const applyVehicleData = (data) => {
    form.setFieldsValue({
      make: data.make,
      model: data.model,
      year: data.year,
      vin: data.vin,
      licensePlate: data.license_plate,
      vehicleType: data.vehicle_type,
      trackingStatus: data.tracking_status,
      navixyApiKey: data.navixy_api_key,
      navixyTrackerId: data.navixy_tracker_id,
      fuelStatus: data.fuel_sensor_status,
      odometer: data.odometer_reading,
      fuelType: data.fuel_type,
      tankcapacity: parseFloat(data.tank_capacity) || undefined,
      fueleconomy: parseFloat(data.fuel_economy) || undefined,
      driver: data.driver?.id,
      jobTitle: data.driver?.job_title,
      address: data.driver?.address,
    });

    setCurrentDriver(data.driver?.id ? data.driver : null);
    setAvatar(resolveAvatarUrl(data.avatar));
    setAvatarFile(null);

    if (data.fuel_card) {
      setCreditData({
        fuel_card_id: data.fuel_card.id,
        is_active: !!data.fuel_card.is_active,
        card_number: data.fuel_card.card_number || "-",
        daily_limit: parseFloat(data.fuel_card.daily_limit) || 0,
        daily_limit_enabled: !!data.fuel_card.daily_limit_enabled,
        monthly_limit: parseFloat(data.fuel_card.monthly_limit) || 0,
        monthly_limit_enabled: !!data.fuel_card.monthly_limit_enabled,
        weekly_limit: parseFloat(data.fuel_card.weekly_limit) || 0,
        weekly_limit_enabled: !!data.fuel_card.weekly_limit_enabled,
        weekend_limit: parseFloat(data.fuel_card.weekend_limit) || 0,
        weekend_limit_enabled: !!data.fuel_card.weekend_limit_enabled,
        allow_any_driver: !!data.fuel_card.allow_any_driver,
        auto_reset: !!data.fuel_card.auto_reset,
      });
    } else {
      setCreditData({
        fuel_card_id: null,
        is_active: false,
        card_number: "-",
        daily_limit: 0,
        daily_limit_enabled: false,
        monthly_limit: 0,
        monthly_limit_enabled: false,
        weekly_limit: 0,
        weekly_limit_enabled: false,
        weekend_limit: 0,
        weekend_limit_enabled: false,
        allow_any_driver: false,
        auto_reset: false,
      });
    }

    if (Array.isArray(data.documents)) {
      setDocuments(
        data.documents.map((doc) => {
          const label = (doc.category || "Document").replace(/_/g, " ");

          const displayLabel = label.charAt(0).toUpperCase() + label.slice(1);

          return {
            key: String(doc.id),
            id: doc.id,
            name: doc.file ? doc.file.split("/").pop() : displayLabel,
            category: displayLabel,
            date: doc.uploaded_at
              ? new Date(doc.uploaded_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "-",
            expiryDate: doc.expiry_date
              ? new Date(doc.expiry_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "-",
            fileUrl: resolveAvatarUrl(doc.file),
          };
        }),
      );
    }
  };

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No vehicle selected. Please open this page from the Vehicles list.",
      );
      return;
    }

    const fetchVehicle = async () => {
      try {
        const res = await getVehicleById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) !== 2 || !data) {
          message.error(res?.data?.message || "Failed to load vehicle.");
          return;
        }

        fetchedVehicleRef.current = data;
        applyVehicleData(data);
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load vehicle.",
        );
      }
    };

    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passedId, form]);

  const handleCancel = () => {
    if (fetchedVehicleRef.current) {
      applyVehicleData(fetchedVehicleRef.current);
    }
  };

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const MAX_FILENAME_LENGTH = 100;

  const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
    if (file.name.length <= maxLength) return file;

    const dotIndex = file.name.lastIndexOf(".");
    const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
    const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
    const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

    return new File([file], `${truncatedBase}${ext}`, { type: file.type });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const shortened = shortenFileName(file);
      setAvatarFile(shortened);

      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(shortened);
    }
  };

  const handleCreditChange = (key, value) => {
    setCreditData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!passedId) {
      message.error("No vehicle selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill in all required fields.");
      return;
    }

    const original = fetchedVehicleRef.current || {};
    const originalFuelCard = original.fuel_card || {};

    const formData = new FormData();
    const appendIfChanged = (key, newVal, oldVal) => {
      const normalizedNew = newVal ?? "";
      const normalizedOld = oldVal ?? "";
      if (String(normalizedNew) !== String(normalizedOld)) {
        formData.append(key, normalizedNew);
      }
    };

    appendIfChanged("make", values.make, original.make);
    appendIfChanged("model", values.model, original.model);
    appendIfChanged("year", values.year, original.year);
    appendIfChanged("vin", values.vin, original.vin);
    appendIfChanged(
      "license_plate",
      values.licensePlate,
      original.license_plate,
    );
    appendIfChanged("vehicle_type", values.vehicleType, original.vehicle_type);
    appendIfChanged(
      "tracking_status",
      values.trackingStatus,
      original.tracking_status,
    );
    if (values.trackingStatus === "linked") {
      appendIfChanged(
        "navixy_api_key",
        values.navixyApiKey,
        original.navixy_api_key,
      );
      appendIfChanged(
        "navixy_tracker_id",
        values.navixyTrackerId,
        original.navixy_tracker_id,
      );
    }
    appendIfChanged(
      "fuel_sensor_status",
      values.fuelStatus,
      original.fuel_sensor_status,
    );
    appendIfChanged(
      "odometer_reading",
      values.odometer,
      original.odometer_reading,
    );
    appendIfChanged(
      "tank_capacity",
      values.tankcapacity,
      parseFloat(original.tank_capacity) || undefined,
    );
    appendIfChanged(
      "fuel_economy",
      values.fueleconomy,
      parseFloat(original.fuel_economy) || undefined,
    );
    if (typeof values.fuelType === "number") {
      appendIfChanged("fuel_type", values.fuelType, original.fuel_type);
    }
    if (typeof values.driver === "number") {
      appendIfChanged("driver", values.driver, original.driver?.id);
    }

    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }

    if (creditData.fuel_card_id) {
      appendIfChanged(
        "fuel_card_id",
        creditData.fuel_card_id,
        originalFuelCard.id,
      );
      appendIfChanged(
        "daily_limit",
        creditData.daily_limit,
        parseFloat(originalFuelCard.daily_limit) || undefined,
      );
      appendIfChanged(
        "daily_limit_enabled",
        creditData.daily_limit_enabled,
        !!originalFuelCard.daily_limit_enabled,
      );
      appendIfChanged(
        "weekly_limit",
        creditData.weekly_limit,
        parseFloat(originalFuelCard.weekly_limit) || undefined,
      );
      appendIfChanged(
        "weekly_limit_enabled",
        creditData.weekly_limit_enabled,
        !!originalFuelCard.weekly_limit_enabled,
      );
      appendIfChanged(
        "monthly_limit",
        creditData.monthly_limit,
        parseFloat(originalFuelCard.monthly_limit) || undefined,
      );
      appendIfChanged(
        "monthly_limit_enabled",
        creditData.monthly_limit_enabled,
        !!originalFuelCard.monthly_limit_enabled,
      );
      appendIfChanged(
        "weekend_limit",
        creditData.weekend_limit,
        parseFloat(originalFuelCard.weekend_limit) || undefined,
      );
      appendIfChanged(
        "weekend_limit_enabled",
        creditData.weekend_limit_enabled,
        !!originalFuelCard.weekend_limit_enabled,
      );
      appendIfChanged(
        "allow_any_driver",
        creditData.allow_any_driver,
        !!originalFuelCard.allow_any_driver,
      );
      appendIfChanged(
        "auto_reset",
        creditData.auto_reset,
        !!originalFuelCard.auto_reset,
      );
    }

    const newDocuments = documents.filter((doc) => doc.file);
    newDocuments.forEach((doc) => {
      formData.append(doc.category, doc.file);
    });
    if (newDocuments.length > 0) {
      const expiryDates = {};
      newDocuments.forEach((doc) => {
        expiryDates[doc.category] = doc.expiryDate || null;
      });
      formData.append("document_expiry_dates", JSON.stringify(expiryDates));
    }

    setSaving(true);
    try {
      const res = await updateVehicle(passedId, formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "Vehicle updated successfully!"),
        );
        navigate("/vehicle-detail", { state: { id: passedId } });
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update vehicle."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update vehicle."),
      );
    } finally {
      setSaving(false);
    }
  };

  const vehicleInformation = [
    {
      name: "make",
      label: "Make",
      type: "text",
      placeholder: "Enter Vehicle Make",
      rules: [{ required: true }],
    },
    {
      name: "model",
      label: "Model",
      type: "text",
      placeholder: "Enter Model",
      rules: [{ required: true }],
    },
    {
      name: "year",
      label: "Year",
      type: "number",
      placeholder: "Enter Year",
      rules: [{ required: true }],
    },
    {
      name: "vin",
      label: "VIN",
      type: "text",
      placeholder: "17-digit Chassis No.",
      rules: [
        { required: true },
        { max: 17, message: "VIN must be at most 17 characters" },
      ],
    },
    {
      name: "licensePlate",
      label: "License Plate",
      type: "text",
      placeholder: "Enter License Plate",
      rules: [{ required: true }],
    },
    {
      name: "vehicleType",
      label: "Vehicle Type",
      type: "select",
      placeholder: "Select Vehicle Type",
      options: vehicleTypeOptions,
      rules: [{ required: true }],
    },
    {
      name: "trackingStatus",
      label: "Tracking System Status",
      description: "Real-time GPS Monitoring",
      type: "segmented",
      options: [
        { label: "Not Linked", value: "not_linked" },
        { label: "Linked", value: "linked" },
      ],
    },
    ...(watchedTrackingStatus === "linked"
      ? [
          {
            name: "navixyApiKey",
            label: "Navixy API Key",
            type: "select",
            placeholder: "Select Navixy API Key",
            options: navixyKeyOptions,
            loading: navixyKeysLoading,
            showSearch: true,
            notFoundContent: "No Navixy API keys saved for your company",
            rules: [{ required: true, message: "Navixy API Key is required" }],
          },
          {
            name: "navixyTrackerId",
            label: "Navixy Tracker ID",
            type: "select",
            placeholder: watchedNavixyKey
              ? "Select Navixy Tracker"
              : "Select an API key first",
            options: trackerOptions,
            loading: trackersLoading,
            showSearch: true,
            disabled: !watchedNavixyKey,
            notFoundContent: "No trackers found for this API key",
            rules: [
              { required: true, message: "Navixy Tracker ID is required" },
            ],
          },
        ]
      : []),
    {
      name: "fuelStatus",
      label: "Fuel Sensor Status",
      description: "High Precision Level Sensor",
      type: "segmented",
      options: [
        { label: "None", value: "none" },
        { label: "Active", value: "active" },
      ],
    },
    {
      name: "odometer",
      label: "Current Odometer Reading (KM)",
      type: "text",
      placeholder: "Enter Current Odometer Reading (KM)",
      extra: odometerNote && (
        <span className={`field-note field-note--${odometerNote.type}`}>
          {odometerNote.text}
        </span>
      ),
      rules: [{ required: true }],
    },
  ];

  const specsInfoFields = [
    {
      name: "fuelType",
      label: "Fuel Type",
      type: "select",
      options: fuelTypeOptions,
      placeholder: "Select Fuel Type",
      rules: [{ required: true, message: "Fuel type is required" }],
    },
    {
      name: "tankcapacity",
      label: "Tank Capacity (Liter)",
      type: "number",
      placeholder: "Enter capacity",
      rules: [{ required: true }],
    },
    {
      name: "fueleconomy",
      label: "Fuel Economy (L/100)",
      type: "number",
      placeholder: "Enter Fuel Economy ",
      rules: [{ required: true }],
    },
  ];

  const operationsInformation = [
    {
      name: "driver",
      label: "Assigned Driver",
      type: "select",
      // The lookup only returns unassigned drivers, so make sure the
      // vehicle's current driver is an option (otherwise its id is shown).
      options:
        currentDriver &&
        !driverOptions.some((o) => o.value === currentDriver.id)
          ? [
              { label: currentDriver.name, value: currentDriver.id },
              ...driverOptions,
            ]
          : driverOptions,
      placeholder: "Select Assigned Driver",
    },
    {
      name: "jobTitle",
      label: "Job Title",
      type: "text",
      placeholder: "Auto-filled from the selected driver",
      disabled: true,
      extra: DRIVER_FIELDS_NOTE,
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      placeholder: "Auto-filled from the selected driver",
      disabled: true,
      extra: DRIVER_FIELDS_NOTE,
    },
  ];

  const limitGridConfig = [
    {
      label: "Daily Spending Limit",
      sub: "Resets at 00:00 UTC",
      key: "daily_limit",
      toggle: "daily_limit_enabled",
    },
    {
      label: "Monthly Spending Limit",
      sub: "Resets 1st of month",
      key: "monthly_limit",
      toggle: "monthly_limit_enabled",
    },
    {
      label: "Weekly Spending Limit",
      sub: "Resets every Monday",
      key: "weekly_limit",
      toggle: "weekly_limit_enabled",
    },
    {
      label: "Weekend Rules",
      sub: "Restricted Sat/Sun",
      key: "weekend_limit",
      toggle: "weekend_limit_enabled",
      accent: "green-accent",
    },
  ];

  return (
    <div className="edit-company vehicle-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div className="header-wrapper">
        <div className="title">Edit Vehicle Details</div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          if ("navixyApiKey" in changed) {
            form.setFieldsValue({ navixyTrackerId: undefined });
            setOdometerNote(null);
          }
          if ("navixyTrackerId" in changed) {
            fetchNavixyOdometer(
              form.getFieldValue("navixyApiKey"),
              changed.navixyTrackerId,
            );
          }
        }}
      >
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>
        </div>

        <InputCard title="Vehicle Information" fields={vehicleInformation} />
        <InputCard title="Technical Specs" fields={specsInfoFields} />

        <div className="credit-controls">
          <div className="header-section">
            <div className="heading">Credit Controls</div>
          </div>

          <div className="control-row">
            <div
              className={`status-card ${creditData.is_active ? "active-card" : ""}`}
            >
              <div className="icon-box">
                <img src={cardIcon} alt="card Icon" />
              </div>
              <div className="wrapper">
                <div className="card-info">
                  <div className="label-main">Active Fuel Card</div>
                  <div className="label-sub">
                    Digital wallet payments enabled
                  </div>
                </div>
                <div>
                  <Switch
                    checked={creditData.is_active}
                    onChange={(val) => handleCreditChange("is_active", val)}
                  />
                </div>
              </div>
            </div>

            <div className="field-display">
              <div className="field-label">
                Fuel Card <span className="required">*</span>
              </div>
              <Input
                className="field-box"
                style={{ width: "100%" }}
                placeholder="No fuel card assigned"
                variant="borderless"
                value={
                  creditData.fuel_card_id ? creditData.card_number || "" : ""
                }
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="limits-grid">
            {limitGridConfig.map((item) => (
              <div className={`limit-card ${item.accent || ""}`} key={item.key}>
                <div className="limit-header">
                  <div>
                    <div className="limit-title">{item.label}</div>
                    <div className="limit-reset">{item.sub}</div>
                  </div>
                  <Switch
                    checked={creditData[item.toggle]}
                    onChange={(val) => handleCreditChange(item.toggle, val)}
                  />
                </div>
                <div className="value-box">
                  <span className="currency">$</span>
                  <InputNumber
                    className="w-full"
                    variant="borderless"
                    controls={false}
                    min={0}
                    disabled={!creditData[item.toggle]}
                    value={creditData[item.key]}
                    onChange={(val) => handleCreditChange(item.key, val)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="advanced-section">
            <div className="section-tag">ADVANCED RULE LOGIC</div>
            <div className="logic-row">
              <div className="logic-item">
                <div className="logic-content">
                  <div className="logic-icon">
                    <img src={refuelIcon} alt="refuel" />
                  </div>
                  <span>Allow Any Authorized Driver to Refuel</span>
                </div>
                <Switch
                  checked={creditData.allow_any_driver}
                  onChange={(val) =>
                    handleCreditChange("allow_any_driver", val)
                  }
                />
              </div>
              <div className="logic-item">
                <div className="logic-content">
                  <div className="logic-icon">
                    <img src={resetIcon} alt="reset" />
                  </div>
                  <span>Auto Reset Limits</span>
                </div>
                <Switch
                  checked={creditData.auto_reset}
                  onChange={(val) => handleCreditChange("auto_reset", val)}
                />
              </div>
            </div>
          </div>
        </div>

        <InputCard title="Operations" fields={operationsInformation} />

        <GenericDocumentManagerTable
          initialDocuments={documents}
          setDocuments={setDocuments}
          documentTypes={documentTypeOptions}
          allowSelectAll={true}
          onDeleteDocument={async (doc) => {
            if (!doc?.id) return;

            const res = await deleteVehicleDocument(doc.id);

            if (Math.floor(res?.status / 100) !== 2) {
              throw new Error(
                getErrorMessage(res?.data, "Failed to delete document."),
              );
            }
          }}
        />

        <div className="btns-row">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditVehicle;
